import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { useRecoilValue } from 'recoil';
import Add from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import isString from 'lodash/isString';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import { AccessLevel } from 'common/graphqlTypes';
import log from 'common/ScalyrLogger';
import history from 'service/customHistory';
import { updateUrlParamsString } from 'common/util/location';
import { useDebouncedValue } from 'hooks/useDebouncedValue';

import Button, { ButtonVariant } from 'components/common/Button/Button';
import { featurePermissionsState } from 'components/account/account.state';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import Page from 'components/common/Page/Page';
import ClearableInput from 'components/common/ClearableInput/ClearableInput';
import { LIST_FULL_DASHBOARDS_V2 } from 'components/dashboards/queries';
import { DashboardConfigType, GraphConfigType } from 'components/dashboards/types';
import DashboardCreator, { AFTER_CREATE } from 'components/dashboards/DashboardCreator';

import { generateParamsList } from '../dashboardData';
import DashboardList, { hasFilterMatches } from './DashboardList';

import './DashboardListPage.scss';

/**
 * This is the page wrapper for the Dashboard page. All it does is get the dashboard config and pass it
 * off to its child components (who will do the real work).
 */
function DashboardListPage(): JSX.Element {
  // The dashboard display name is the same as the config filename, which we get from a query param
  const incomingFilter = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_FILTER));
  const [filterText, setFilterText] = useState(incomingFilter);
  const featurePermissions = useRecoilValue(featurePermissionsState);
  const hasWriteAccess = featurePermissions.dashboards === AccessLevel.WRITE;

  const [showCreateDashDialog, setCreateDashDialog] = useState(false);
  const { loading, error, data } = useQuery(LIST_FULL_DASHBOARDS_V2);

  const debouncedFilterText = useDebouncedValue(filterText, 500);
  useConditionalEffect(() => {
    history.replace({
      pathname: K.ROUTE.DASHBOARD_LIST,
      search: updateUrlParamsString(window.location.search, {
        [K.URL_PARAM.DASHBOARD_FILTER]: debouncedFilterText,
      } as UrlParams),
    });
  }, debouncedFilterText !== usePrevious(debouncedFilterText));

  if (loading) {
    return (
      <Page containerModifier="DashboardListPage" docTitle="Dashboards" type="full">
        <div className="DashboardListPage">
          <LoadingPlaceholder />
        </div>
      </Page>
    );
  }

  if (error) {
    log.error('Unable to load list of dashboards.');
    return <ErrorPlaceholder error={error} />;
  }

  let dashboardConfigs: Array<DashboardConfigType> = [];
  let currentDashboardName = '';
  try {
    dashboardConfigs = data.dashboardsV2.map((dashboard: DashboardConfigType) => {
      currentDashboardName = dashboard.name;
      const tabs = dashboard.tabs?.length
        ? dashboard.tabs.map((tab) => ({
            ...tab,
            params: generateParamsList(
              isString(tab.parameters) && tab.parameters?.length > 0 ? JSON.parse(tab.parameters) : [],
              null,
            ),
            graphs: tab.graphs?.length ? JSON.parse(tab.graphs as string) : [],
          }))
        : [];
      return {
        ...dashboard,
        tabs,
      };
    });
  } catch (ex) {
    return (
      <ErrorPlaceholder
        error={{
          message: `Sorry, but there is a syntax error with the JSON for the ${currentDashboardName} dashboard. Please fix it and try again.`,
        }}
      />
    );
  }

  // We only highlight words that starts with the filter text, not in the middle
  const matchingDashboards = dashboardConfigs.filter((config) => {
    if (hasFilterMatches(config.name, filterText)) {
      return true;
    }

    if (config.tabs) {
      for (const tab of config.tabs) {
        for (const graph of tab.graphs as GraphConfigType[]) {
          if (graph.title && hasFilterMatches(graph.title, filterText)) {
            return true;
          }

          if (graph.label && hasFilterMatches(graph.label, filterText)) {
            return true;
          }
        }
      }
    } else {
      // This is an over-reaching restriction, as these are small arrays which we want to return out of
      for (const graph of config.graphs as GraphConfigType[]) {
        if (graph.title && hasFilterMatches(graph.title, filterText)) {
          return true;
        }

        if (graph.label && hasFilterMatches(graph.label, filterText)) {
          return true;
        }
      }
    }

    return false;
  });

  return (
    <Page containerModifier="DashboardListPage" docTitle="All Dashboards" type="full">
      <div className="DashboardListPage__Header">
        <div className="DashboardListPage__Title">Dashboards</div>
        <div className="DashboardListPage__TitleCount">{matchingDashboards.length}</div>
        <div className="DashboardListPage__Filter" data-test-id={ELEMENT.dashboardsListFilter}>
          <SearchIcon className="DashboardListPage__SearchIcon" />
          <ClearableInput
            autoFocus
            className="form-control"
            placeholder="Filter dashboards"
            onChange={setFilterText}
            value={filterText}
          />
        </div>
        {hasWriteAccess && (
          <Button
            onClick={(): void => setCreateDashDialog(true)}
            variant={ButtonVariant.SECONDARY}
            className="DashboardListPage__NewDashboard"
            leftIcon={<Add fontSize="small" />}
          >
            New Dashboard
          </Button>
        )}
      </div>
      <DashboardList configs={matchingDashboards} filterText={filterText.toLowerCase()} />
      {hasWriteAccess && (
        <DashboardCreator
          showDialog={showCreateDashDialog}
          afterCreate={AFTER_CREATE.GOTO}
          dialogClose={(): void => setCreateDashDialog(false)}
        />
      )}
    </Page>
  );
}

export default DashboardListPage;
